import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../../home/components/Footer';

export default function HVACPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openScheduler = () => {
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (widget) widget.click();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="cursor-pointer">
              <div className="text-2xl font-bold leading-tight text-[#2C2C2C]">RW Construction</div>
              <div className="text-sm leading-tight text-gray-600">Las Vegas LLC</div>
            </Link>
            <div className="hidden lg:flex items-center gap-8">
              <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Home</Link>
              <Link to="/services/pool-repair" className="text-sm font-medium text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Pool Repair</Link>
              <Link to="/services/roofing" className="text-sm font-medium text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Roofing</Link>
              <Link to="/services/hvac" className="text-sm font-medium text-[#FF6B35] transition-colors cursor-pointer">HVAC</Link>
              <Link to="/services/synthetic-grass" className="text-sm font-medium text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Synthetic Grass</Link>
              <Link to="/services/pavers" className="text-sm font-medium text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Pavers</Link>
              <button onClick={openScheduler} className="bg-[#FF6B35] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#E55A2B] transition-colors whitespace-nowrap cursor-pointer">
                Get Free Estimate
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/89ec838b8f176572a28a210d2fd8548d.jpg"
            alt="Professional HVAC Services Las Vegas"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        </div>
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <div className="inline-flex items-center gap-2 bg-[#FF6B35] px-4 py-2 rounded-full mb-6">
            <i className="ri-temp-cold-line text-white"></i>
            <span className="text-white text-sm font-semibold">Expert HVAC Services</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            HVAC & Air Conditioning Services
          </h1>
          <p className="text-white/90 text-lg leading-relaxed mb-8 max-w-3xl mx-auto">
            Stay cool in the Las Vegas heat with our professional HVAC services. From AC installation and repair to complete system replacements, our licensed technicians keep your home comfortable year-round.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={openScheduler}
              className="inline-flex items-center gap-2 bg-[#FF6B35] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#E55A2B] transition-all hover:scale-105 cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-calendar-schedule-line text-xl"></i>
              Schedule AC Service
            </button>
            <a
              href="tel:7025001790"
              className="inline-flex items-center gap-3 bg-white text-orange-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-2xl"></i>
              Call 702-500-1790
            </a>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">Our Expertise</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">Comprehensive HVAC Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">We provide complete heating, ventilation, and air conditioning solutions to keep your Las Vegas home comfortable in every season.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: 'ri-temp-cold-line', title: 'AC Installation', desc: 'Professional installation of high-efficiency air conditioning systems designed for the extreme Las Vegas heat.', features: ['Central AC systems', 'Ductless mini-splits', 'Energy-efficient units', 'Proper sizing'] },
              { icon: 'ri-tools-line', title: 'AC Repair', desc: 'Fast, reliable repairs to get your cooling system back up and running when you need it most.', features: ['Same-day service', 'All brands serviced', 'Refrigerant recharge', 'Compressor repair'] },
              { icon: 'ri-refresh-line', title: 'System Replacement', desc: 'Upgrade to a modern, energy-efficient HVAC system and save on utility bills while improving comfort.', features: ['Free estimates', 'Financing available', 'Rebate assistance', 'Warranty included'] },
              { icon: 'ri-calendar-check-line', title: 'Preventive Maintenance', desc: 'Regular maintenance keeps your system running efficiently and prevents costly breakdowns.', features: ['Tune-ups', 'Filter replacement', 'Coil cleaning', 'Performance check'] },
              { icon: 'ri-fire-line', title: 'Heating Services', desc: 'Complete heating solutions including furnace repair, installation, and heat pump services.', features: ['Furnace repair', 'Heat pumps', 'Heating installation', 'Thermostat setup'] },
              { icon: 'ri-alarm-warning-line', title: 'Emergency Service', desc: '24/7 emergency HVAC repairs because we know AC problems can\'t wait in the desert heat.', features: ['24/7 availability', 'Fast response', 'No overtime fees', 'Immediate relief'] },
            ].map((service, index) => (
              <div key={index} className="bg-[#F8F6F3] rounded-2xl p-8 hover:shadow-xl transition-all group">
                <div className="w-16 h-16 bg-[#FF6B35] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${service.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#2C2C2C] mb-3">{service.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{service.desc}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-[#FF6B35]"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why AC Matters in Las Vegas */}
      <section className="py-20 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20high%20efficiency%20air%20conditioning%20condenser%20unit%20installed%20outside%20beautiful%20Las%20Vegas%20home%2C%20clean%20white%20AC%20unit%20with%20copper%20pipes%2C%20professional%20HVAC%20installation%2C%20desert%20landscaping%20background%2C%20sunny%20day%20photography%20showing%20quality%20workmanship&width=600&height=700&seq=hvac-unit-001&orientation=portrait"
                alt="High Efficiency AC Unit Las Vegas"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">Desert Climate Experts</span>
              <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-6">Why AC is Essential in Las Vegas</h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                With summer temperatures regularly exceeding 110°F, a reliable air conditioning system isn't a luxury in Las Vegas—it's a necessity. Our HVAC experts understand the unique demands of desert climate cooling.
              </p>
              <div className="space-y-4">
                {[
                  { icon: 'ri-sun-line', title: 'Extreme Heat Protection', desc: 'Systems designed to handle 115°F+ temperatures' },
                  { icon: 'ri-leaf-line', title: 'Energy Efficiency', desc: 'High SEER ratings to reduce cooling costs' },
                  { icon: 'ri-drop-line', title: 'Low Humidity Control', desc: 'Proper moisture balance for comfort' },
                  { icon: 'ri-shield-check-line', title: 'Dust & Air Quality', desc: 'Advanced filtration for desert conditions' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4 bg-white rounded-xl p-4">
                    <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-[#FF6B35] text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[#2C2C2C]">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brands We Service */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">Trusted Brands</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">Brands We Install & Service</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">We work with all major HVAC brands and install only top-quality equipment.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {[
              'Carrier', 'Trane', 'Lennox', 'Rheem', 'Goodman', 'American Standard',
              'Bryant', 'York', 'Daikin', 'Mitsubishi', 'Amana', 'Ruud'
            ].map((brand, index) => (
              <div key={index} className="bg-[#F8F6F3] rounded-xl p-6 text-center hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-checkbox-circle-line text-[#FF6B35] text-2xl"></i>
                </div>
                <span className="text-[#2C2C2C] font-semibold text-sm">{brand}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">How It Works</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">Our HVAC Service Process</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">We follow a thorough process to ensure your HVAC system performs at its best.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Free Consultation', desc: 'We assess your cooling needs and inspect your current system to provide expert recommendations.' },
              { step: '02', title: 'Detailed Quote', desc: 'Receive transparent pricing with options for different efficiency levels and financing.' },
              { step: '03', title: 'Professional Install', desc: 'Our certified technicians complete the work with precision and minimal disruption.' },
              { step: '04', title: 'System Testing', desc: 'We thoroughly test your system and walk you through operation and maintenance.' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-[#FF6B35] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white text-2xl font-bold">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-[#2C2C2C] mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">Our Work</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">Recent HVAC Projects</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">See the quality HVAC installations we deliver for Las Vegas homeowners.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { img: 'https://readdy.ai/api/search-image?query=New%20central%20air%20conditioning%20system%20installation%20in%20Las%20Vegas%20home%2C%20modern%20high%20efficiency%20AC%20unit%20with%20ductwork%2C%20professional%20HVAC%20installation%2C%20clean%20finished%20work%20in%20residential%20attic%20space%2C%20quality%20craftsmanship%20photography&width=600&height=450&seq=hvac-work-001&orientation=landscape', title: 'Central AC Installation', location: 'Henderson, NV' },
              { img: 'https://readdy.ai/api/search-image?query=Ductless%20mini%20split%20air%20conditioning%20installation%20in%20modern%20Las%20Vegas%20home%2C%20sleek%20wall%20mounted%20indoor%20unit%2C%20energy%20efficient%20cooling%20system%2C%20professional%20HVAC%20work%2C%20clean%20contemporary%20interior%20design&width=600&height=450&seq=hvac-work-002&orientation=landscape', title: 'Ductless Mini-Split', location: 'Summerlin, NV' },
              { img: 'https://readdy.ai/api/search-image?query=Complete%20HVAC%20system%20replacement%20with%20new%20furnace%20and%20AC%20condenser%20unit%2C%20professional%20installation%20in%20Las%20Vegas%20residential%20home%2C%20modern%20high%20efficiency%20heating%20and%20cooling%20equipment%2C%20clean%20mechanical%20room%20setup&width=600&height=450&seq=hvac-work-003&orientation=landscape', title: 'Full System Replacement', location: 'Las Vegas, NV' },
            ].map((project, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="rounded-2xl overflow-hidden mb-4 shadow-lg">
                  <img
                    src={project.img}
                    alt={project.title}
                    className="w-full h-64 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-lg font-bold text-[#2C2C2C]">{project.title}</h3>
                <p className="text-gray-500 text-sm flex items-center gap-1">
                  <i className="ri-map-pin-line text-[#FF6B35]"></i>
                  {project.location}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#2C2C2C]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">Why Choose Us</span>
              <h2 className="text-4xl font-bold text-white mt-3 mb-6">Las Vegas HVAC Experts</h2>
              <p className="text-white/80 text-base leading-relaxed mb-8">
                With over 20 years of experience in the Las Vegas Valley, we understand the unique demands of desert climate cooling. Our certified technicians deliver reliable, efficient HVAC solutions that keep your home comfortable even in extreme heat.
              </p>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: 'ri-medal-line', title: '20+ Years', desc: 'Experience' },
                  { icon: 'ri-shield-star-line', title: 'Licensed', desc: '& Insured' },
                  { icon: 'ri-temp-cold-line', title: '5000+', desc: 'AC Units Serviced' },
                  { icon: 'ri-24-hours-line', title: '24/7', desc: 'Emergency Service' },
                ].map((stat, index) => (
                  <div key={index} className="bg-white/10 rounded-xl p-5">
                    <i className={`${stat.icon} text-[#FF6B35] text-3xl mb-2 block`}></i>
                    <div className="text-white font-bold text-xl">{stat.title}</div>
                    <div className="text-white/60 text-sm">{stat.desc}</div>
                  </div>
                ))}
              </div>
              {/* Certifications */}
              <div className="mt-8">
                <h3 className="text-white font-semibold mb-4">Our Certifications</h3>
                <div className="flex flex-wrap gap-3">
                  {[
                    'NATE Certified',
                    'EPA 608 Certified',
                    'ACCA Member',
                    'AHRI Certified',
                    'Energy Star Partner'
                  ].map((cert, index) => (
                    <div key={index} className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                      <i className="ri-award-fill text-[#FF6B35]"></i>
                      <span className="text-white text-sm">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20HVAC%20technician%20in%20uniform%20working%20on%20air%20conditioning%20unit%2C%20skilled%20tradesman%20with%20tools%20and%20safety%20equipment%2C%20friendly%20service%20professional%2C%20Las%20Vegas%20residential%20home%20background%2C%20high%20quality%20portrait%20photography%20showing%20expert%20at%20work&width=600&height=700&seq=hvac-tech-001&orientation=portrait"
                alt="Professional HVAC Technician Las Vegas"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#FF6B35] text-sm font-semibold tracking-wider uppercase">FAQ</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">HVAC Questions</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: 'How often should I service my AC in Las Vegas?', a: 'We recommend servicing your AC twice a year—once in spring before the hot season and once in fall. Regular maintenance extends system life and prevents costly breakdowns during peak heat.' },
              { q: 'What size AC do I need for my Las Vegas home?', a: 'AC sizing depends on square footage, insulation, windows, and other factors. Our technicians perform a load calculation to recommend the right size system for optimal efficiency and comfort.' },
              { q: 'How long do AC units last in the Las Vegas heat?', a: 'With proper maintenance, AC units typically last 12-15 years in Las Vegas. The extreme heat can shorten lifespan, so regular tune-ups are essential to maximize your investment.' },
              { q: 'What SEER rating should I look for?', a: 'For Las Vegas, we recommend at least 16 SEER for good efficiency. Higher SEER ratings (18-26) cost more upfront but provide significant energy savings in our hot climate.' },
            ].map((faq, index) => (
              <div key={index} className="bg-[#F8F6F3] rounded-xl p-6">
                <h3 className="text-lg font-bold text-[#2C2C2C] mb-2">{faq.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-red-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Stay Cool This Summer</h2>
          <p className="text-white/90 text-lg mb-8">
            Get a free AC inspection and estimate from Las Vegas's trusted HVAC experts.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={openScheduler}
              className="inline-flex items-center gap-2 bg-white text-[#FF6B35] px-8 py-4 rounded-full text-base font-semibold hover:bg-gray-100 transition-all hover:scale-105 cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-calendar-schedule-line text-xl"></i>
              Schedule Free Estimate
            </button>
            <a
              href="tel:7025001790"
              className="inline-flex items-center gap-3 bg-white text-orange-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-2xl"></i>
              Call 702-500-1790 Today
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
